import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Link,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import LoginForm from '../../components/Auth/LoginForm';
import { PublicEventCalendar } from '../../components/events/PublicEventCalendar';
import { PublicCompanyGrid } from '../../components/LoginPage/PublicCompanyGrid';

const SCROLL_BOTTOM_THRESHOLD_PX = 48;

type GuideMode = 'events' | 'companies' | 'top';

const GUIDE_CONFIG: Record<
  GuideMode,
  { label: string; ariaLabel: string; direction: 'down' | 'up' }
> = {
  events: {
    label: '開催\nイベント',
    ariaLabel: '開催イベントを見る',
    direction: 'down',
  },
  companies: {
    label: '参加\n企業',
    ariaLabel: '参加企業を見る',
    direction: 'down',
  },
  top: {
    label: 'ページ\nトップへ',
    ariaLabel: 'ページトップへ戻る',
    direction: 'up',
  },
};

const bounceDownSx = {
  '@keyframes eventGuideBounceDown': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(4px)' },
  },
  animation: 'eventGuideBounceDown 1.5s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

const bounceUpSx = {
  '@keyframes eventGuideBounceUp': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-4px)' },
  },
  animation: 'eventGuideBounceUp 1.5s ease-in-out infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const calendarSectionRef = useRef<HTMLDivElement>(null);
  const companiesSectionRef = useRef<HTMLDivElement>(null);
  const [guideMode, setGuideMode] = useState<GuideMode>('events');

  const scrollToCalendar = useCallback(() => {
    calendarSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToCompanies = useCallback(() => {
    companiesSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToPageTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const updateGuideMode = useCallback(() => {
    const { scrollY, innerHeight } = window;
    const docHeight = document.documentElement.scrollHeight;
    const atBottom = scrollY + innerHeight >= docHeight - SCROLL_BOTTOM_THRESHOLD_PX;

    if (atBottom) {
      setGuideMode('top');
      return;
    }

    const calendarTop = calendarSectionRef.current?.offsetTop ?? 0;
    if (scrollY < calendarTop - 80) {
      setGuideMode('events');
      return;
    }

    setGuideMode('companies');
  }, []);

  useEffect(() => {
    updateGuideMode();
    window.addEventListener('scroll', updateGuideMode, { passive: true });
    window.addEventListener('resize', updateGuideMode);
    return () => {
      window.removeEventListener('scroll', updateGuideMode);
      window.removeEventListener('resize', updateGuideMode);
    };
  }, [updateGuideMode]);

  const handleGuideAction = useCallback(() => {
    if (guideMode === 'top') {
      scrollToPageTop();
    } else if (guideMode === 'companies') {
      scrollToCompanies();
    } else {
      scrollToCalendar();
    }
  }, [guideMode, scrollToCalendar, scrollToCompanies, scrollToPageTop]);

  const handleGuideKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleGuideAction();
      }
    },
    [handleGuideAction],
  );

  const guide = GUIDE_CONFIG[guideMode];

  const handleForgotPassword = () => navigate('/auth/forgot-password');
  const handleRegister       = () => navigate('/auth/register');
  const handleCompanyLogin   = () => navigate('/company/login');

  const handleLoginRequired = useCallback(
    (_companyId?: number) => {
      navigate('/auth/register');
    },
    [navigate]
  );

  return (
    <>
      {/* 右下 FAB（PC・モバイル共通）— フォーム中央と被らず、カード幅も変えない */}
      <Box
        component="button"
        type="button"
        onClick={handleGuideAction}
        onKeyDown={handleGuideKeyDown}
        aria-label={guide.ariaLabel}
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 20, md: 24 },
          bottom: {
            xs: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            md: 24,
          },
          zIndex: 1100,
          width: { xs: 72, md: 96 },
          height: { xs: 72, md: 96 },
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.25, md: 0.5 },
          p: { xs: 0.75, md: 1 },
          background: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.18)',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': {
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.22)',
            transform: 'scale(1.03)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: { xs: '0.62rem', md: '0.75rem' },
            fontWeight: 600,
            color: '#5a4d8a',
            letterSpacing: '0.02em',
            lineHeight: 1.25,
            textAlign: 'center',
            whiteSpace: 'pre-line',
          }}
        >
          {guide.label}
        </Typography>
        {guide.direction === 'up' ? (
          <KeyboardArrowUpIcon
            sx={{ color: '#667eea', fontSize: { xs: 22, md: 28 }, ...bounceUpSx }}
          />
        ) : (
          <KeyboardArrowDownIcon
            sx={{ color: '#667eea', fontSize: { xs: 22, md: 28 }, ...bounceDownSx }}
          />
        )}
      </Box>

      {/* ─── グラデーション背景（ログインカード） ─── */}
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
          pb: { xs: 4, sm: 5 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
            width: '100%',
          }}
        >
          <Card
            elevation={8}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
              <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 5 } }}>
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 'bold',
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: 'clamp(1.3rem, 6vw, 2rem) !important',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ガクフドキャリア
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    fontSize: 'clamp(1rem, 4vw, 1.3rem) !important',
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  学生ログイン
                </Typography>
              </Box>

              <LoginForm
                onForgotPassword={handleForgotPassword}
                onRegister={handleRegister}
                onCompanyLogin={handleCompanyLogin}
              />
            </CardContent>
          </Card>
        </Container>
      </Box>

      <Box ref={calendarSectionRef} sx={{ background: '#F5F5F5', py: 6 }}>
        <Container maxWidth={false} sx={{ maxWidth: { xs: '100%', sm: '720px', md: '800px' }, mx: 'auto' }}>
          <PublicEventCalendar />
        </Container>

        {/* ─── 企業一覧（Pinterest風グリッド） ─── */}
        <Container
          ref={companiesSectionRef}
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '720px', md: '1100px' },
            mx: 'auto',
            px: { xs: 2, sm: 3 },
          }}
        >
          <PublicCompanyGrid onDetailsClick={handleLoginRequired} mode="public" limit={8} />
        </Container>

        <Box sx={{ textAlign: 'center', mt: { xs: 4, sm: 5 }, px: 2, pb: { xs: 2, md: 0 } }}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: 1 }}
          >
            © {currentYear} ガクフドキャリア. All rights reserved.
          </Typography>
          <Link
            href="/privacy-policy"
            sx={{
              color: 'text.secondary',
              opacity: 0.8,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline', opacity: 1 },
            }}
          >
            プライバシーポリシー
          </Link>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
